/**
 * ProcessTabBar — sheet tab labels for Sheets engine nodes in Tool view.
 *
 * Shows tab names from engine_metadata (input_sheet / output_sheet).
 * Also includes prev/next node navigation and open-in-new-tab.
 * v1: tab names as info badges (no in-iframe tab switching).
 */

import React from 'react';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Sheet,
} from 'lucide-react';
import type { NodeDefinition } from '../../types/modeling';
import { isSheetsNode } from '../../types/modeling';

interface ProcessTabBarProps {
  node: NodeDefinition;
  onPrevNode: () => void;
  onNextNode: () => void;
  hasPrev: boolean;
  hasNext: boolean;
}

export default function ProcessTabBar({
  node,
  onPrevNode,
  onNextNode,
  hasPrev,
  hasNext,
}: ProcessTabBarProps) {
  const sheetsNode = isSheetsNode(node);
  const metadata = node.metadata as Record<string, any>;

  const inputSheet = metadata?.input_sheet;
  const outputSheet = metadata?.output_sheet;
  const spreadsheetUrl = metadata?.spreadsheet_url;

  return (
    <div className="flex items-center h-10 px-3 bg-bg-warm border-t border-border-default flex-shrink-0 gap-2">
      {/* Prev/Next node navigation */}
      <div className="flex items-center gap-1">
        <button
          onClick={onPrevNode}
          disabled={!hasPrev}
          className={`p-1 rounded transition-colors ${
            hasPrev ? 'text-fg-tertiary hover:bg-bg-warm' : 'text-fg-secondary cursor-not-allowed'
          }`}
          title="Previous node (←)"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          onClick={onNextNode}
          disabled={!hasNext}
          className={`p-1 rounded transition-colors ${
            hasNext ? 'text-fg-tertiary hover:bg-bg-warm' : 'text-fg-secondary cursor-not-allowed'
          }`}
          title="Next node (→)"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Sheet tab badges (Sheets nodes only) */}
      {sheetsNode && (inputSheet || outputSheet) && (
        <div className="flex items-center gap-1.5 flex-1">
          <Sheet className="w-3.5 h-3.5 text-fg-tertiary" />
          {inputSheet && (
            <span className="px-2 py-0.5 text-[10px] font-medium text-success bg-success-bg border border-success rounded">
              IN: {inputSheet}
            </span>
          )}
          {outputSheet && (
            <span className="px-2 py-0.5 text-[10px] font-medium text-accent-hover bg-accent-tint border border-accent-border rounded">
              OUT: {outputSheet}
            </span>
          )}
        </div>
      )}

      {!sheetsNode && <div className="flex-1" />}

      {/* Open in new tab */}
      {spreadsheetUrl && (
        <a
          href={spreadsheetUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-fg-tertiary hover:text-accent transition-colors"
        >
          <ExternalLink className="w-3 h-3" />
          Open in new tab
        </a>
      )}
    </div>
  );
}
